import { computed, inject, Injectable, signal } from '@angular/core';
import { DriverAPIService } from '@core/driver/driver.api';
import {
  AddExportReceiverRequest,
  AutomaticExportRequest,
  BackendProvider,
  ChargePoint,
  ChargePointDetail,
  ChargingStationBackendData,
  ChargingStationBackendDataRequest,
  CreatePricePolicyRequest,
  Driver,
  DriverExtended,
  DriverInvitePayload,
  DriverKey,
  DriverKeyFull,
  DriverOrganization,
  EdiKey,
  Export,
  FlatratePrice,
  LatestPriceResponse,
  OrganizationPricePolicy,
  Price,
  Reimbursement,
  UpdateChargeAssignSettingsRequest,
  UpdateMileageSettingsRequest,
} from '@core/driver/driver.models';
import { ErrorHandler } from '@core/handler/error.handler';
import { ChargeRepayErrorCodes } from '@shared/errors/error.codes';
import { CRAPIError, CRError } from '@shared/models/error.models';
import {
  catchError,
  concat,
  EMPTY,
  ignoreElements,
  interval,
  map,
  Observable,
  startWith,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class DriverStore {
  private readonly api = inject(DriverAPIService);
  private readonly errorHandler = inject(ErrorHandler);

  public readonly isInitialized = signal(false);
  public readonly drivers = signal<Driver[]>([]);
  public readonly chargePoints = signal<ChargePoint[]>([]);
  public readonly organization = signal<DriverOrganization | null>(null);
  public readonly flatratePrices = signal<FlatratePrice[] | null>(null);
  public readonly organizationKeys = signal<EdiKey[]>([]);
  public readonly backendProvider = signal<BackendProvider | null>(null);
  public readonly exports = signal<Export[]>([]);
  public readonly reimbursements = signal<Reimbursement[]>([]);
  public readonly currentDriverKeys = signal<DriverKeyFull[]>([]);
  public readonly currentDriverLatestPrice = signal<LatestPriceResponse | null>(null);
  public readonly currentDriverChargingStationBackendData =
    signal<ChargingStationBackendData | null>(null);
  public readonly currentDriverChargePointDetail = signal<ChargePointDetail | null>(null);

  // will create the drivers extended array whenever the drivers or chargepoints signals update
  public readonly driversExtended = computed<DriverExtended[]>(() =>
    this.calculateExtendedDrivers(),
  );

  public initialize() {
    if (this.isInitialized()) {
      return EMPTY as Observable<void>;
    }

    return concat(
      this.createDrivers$(),
      this.createChargePoints$(),
      this.createDriverOrganization$(),
      this.createOrganizationKeys$(),
      this.createBackendProvider$(),
      this.createExports$(),
    ).pipe(
      tap({ complete: () => this.isInitialized.set(true) }),
      catchError((error) => {
        console.error('Error initializing Driver store:', error);
        return throwError(() => error);
      }),
    );
  }

  // Reloads driver data from the API, for example after an organization switch.
  public reinitialize() {
    return concat(
      this.createDrivers$(),
      this.createChargePoints$(),
      this.createDriverOrganization$(),
      this.createOrganizationKeys$(),
      this.createBackendProvider$(),
      this.createExports$(),
    ).pipe(
      catchError((error) => {
        console.error('Error reinitializing Driver store:', error);
        return throwError(() => error);
      }),
    );
  }

  // Polls charge-point online state while one is present. The caller must unsubscribe.
  public deviceStatePollingClient(uuid: string) {
    return interval(environment.connectionStatePollingInterval).pipe(
      startWith(0),
      switchMap(() =>
        this.api.getConnectionState(uuid).pipe(
          // Keep the interval alive; a single failed poll must not kill the client
          catchError((error) => {
            console.error('Error polling device connection state:', error);
            return EMPTY;
          }),
        ),
      ),
    );
  }

  public inviteSingleDriver(payload: DriverInvitePayload) {
    // Skip the invite when this driver is already invited locally.
    const exists = this.drivers().find((d) => d.email === payload.user_email);

    if (exists) {
      const inviteDriverError: CRError = {
        errorCode: ChargeRepayErrorCodes.INVITATION_USER_ALREADY_INVITED,
        severity: 'error',
      };

      return throwError(() => inviteDriverError);
    } else {
      return this.api.sendSingleInvite(payload).pipe(
        tap(() => {
          setTimeout(() => this.createDrivers$().subscribe(), 2000);
        }),
        catchError(() => {
          const formattedError: CRError = {
            errorCode: ChargeRepayErrorCodes.INVITE_DRIVER_ERROR,
            severity: 'error',
          };
          return throwError(() => formattedError);
        }),
      );
    }
  }

  public inviteBulkDrivers(payloads: DriverInvitePayload[]) {
    return this.api.sendBulkInvite(payloads).pipe(
      tap(() => {
        setTimeout(() => this.createDrivers$().subscribe(), 2000);
      }),
      catchError(() => {
        const formattedError: CRError = {
          errorCode: ChargeRepayErrorCodes.INVITE_DRIVER_ERROR,
          severity: 'error',
        };
        return throwError(() => formattedError);
      }),
    );
  }

  // Only pending invitations can be removed.
  public deleteInvitedDriver(userId: string) {
    const driver = this.drivers().find((d) => d.user_id === userId);
    if (!driver?.pending) {
      const notPendingError: CRError = {
        errorCode: ChargeRepayErrorCodes.DELETE_DRIVER_INVITATION_ERROR,
        severity: 'error',
      };
      return throwError(() => notPendingError);
    }

    return this.api.deleteInvitedDriver(userId).pipe(
      tap(() => {
        this.drivers.update((drivers) => drivers.filter((d) => d.user_id !== userId));
      }),
      catchError((error: CRAPIError) => {
        const formattedError: CRError = {
          errorCode:
            error?.error_code === ChargeRepayErrorCodes.USER_NOT_FOUND_ERROR
              ? ChargeRepayErrorCodes.USER_NOT_FOUND_ERROR
              : ChargeRepayErrorCodes.DELETE_DRIVER_INVITATION_ERROR,
          severity: 'error',
        };
        return throwError(() => formattedError);
      }),
    );
  }

  public downloadCSVExample() {
    this.api.getCSVExample().subscribe((csv) => {
      const blob = csv.body!;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'example.csv';
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  public updateCalibrationLaw($event: boolean) {
    return this.api.updateOrganization({ eichrecht_charging: $event }).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const calibLawError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_CALIBRATION_LAW_ERROR,
          severity: 'error',
        };
        return throwError(() => calibLawError);
      }),
    );
  }

  public updateRfidCharging($event: boolean) {
    return this.api.updateOrganization({ direct_ocpp_idtag_assign_charges: $event }).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const rfidChargingError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_RFID_CHARGING_ERROR,
          severity: 'error',
        };
        return throwError(() => rfidChargingError);
      }),
    );
  }

  public updateAutomaticCompanyCar($event: boolean) {
    return this.api.updateOrganization({ charge_assign_allow_automatic_company_car: $event }).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const automaticCompanyCarError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_AUTOMATIC_COMPANY_CAR_ERROR,
          severity: 'error',
        };
        return throwError(() => automaticCompanyCarError);
      }),
    );
  }

  public updatePaymentProvider(enabled: boolean) {
    return this.api.updateOrganization({ reimbursement_activated: enabled }).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const calibLawError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_PAYMENT_PROVIDER_ERROR,
          severity: 'error',
        };
        return throwError(() => calibLawError);
      }),
    );
  }

  public updateMileageRequest(enabled: boolean) {
    return this.api.updateOrganization({ mileage_request_activated: enabled }).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const mileageRequestError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_MILEAGE_REQUEST_ERROR,
          severity: 'error',
        };
        return throwError(() => mileageRequestError);
      }),
    );
  }

  // dayOfMonth is sent only when it was previously unset (0).
  public updateAutoExport(active: boolean, dayOfMonth?: number) {
    const request = {
      automatic_export_activated: active,
      ...(dayOfMonth && { automatic_export_day_of_month: dayOfMonth }),
    };

    // Store the previous state to restore it on error
    const previousOrganization = this.organization();

    // Update the organization signal first so toggles can revert on error.
    this.organization.update((org) => (org ? { ...org, automatic_export_activated: active } : org));

    return this.api.updateOrganization(request).pipe(
      // Even though updating optistimistically we still set the organization from the response to make sure
      // we have the latest state from the backend
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError((apiError) => {
        // Restore the previous state on error
        if (previousOrganization) {
          console.log(
            'Reverting automatic export activation to previous state',
            previousOrganization,
          );
          this.organization.set({ ...previousOrganization });
        }

        // This setting can not be activated if the current charging assign time is higher than the previously set day of month for the export.
        // In this case a specific error is thrown from the backend which we map here to a proper CRError
        if (apiError.error_code === 'emobility.CHARGE_ASSIGN_TIME_AUTOMATIC_EXPORT_MISMATCH') {
          const autoExportMissmatchError: CRError = {
            errorCode: ChargeRepayErrorCodes.CHARGE_ASSIGN_TIME_AUTOMATIC_EXPORT_MISMATCH,
            severity: 'error',
          };
          return throwError(() => autoExportMissmatchError);
        }

        const calibLawError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_CALIBRATION_LAW_ERROR,
          severity: 'error',
        };
        return throwError(() => calibLawError);
      }),
    );
  }

  public updateExportReceivers(emails: string[]) {
    console.debug('Updating export receivers to:', emails);
    const request: AddExportReceiverRequest = {
      automatic_export_emails: emails,
    };

    return this.api.updateOrganization(request).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const updateExportReceiversError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_EXPORT_RECEIVERS_ERROR,
          severity: 'error',
        };
        return throwError(() => updateExportReceiversError);
      }),
    );
  }

  public updateAutoExportSettings(request: AutomaticExportRequest) {
    console.debug('Updating automatic export to:', request);

    return this.api.updateOrganization(request).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const updateExportSettingsError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_EXPORT_RECEIVERS_ERROR,
          severity: 'error',
        };
        return throwError(() => updateExportSettingsError);
      }),
    );
  }

  public updateExternalBackendProvider(enabled: boolean) {
    return this.api.updateOrganization({ backend_provider_driver_idtag: enabled }).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const calibLawError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_EXTERNAL_BACKEND_PROVIDER_ERROR,
          severity: 'error',
        };
        return throwError(() => calibLawError);
      }),
    );
  }

  public createBackendProvider(payload: Partial<BackendProvider>) {
    return this.api.createBackendProvider(payload).pipe(
      tap((resp) => {
        this.backendProvider.set(resp);
      }),
      catchError(() => {
        const backendProviderUpdateError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_BACKEND_PROVIDER_ERROR,
          severity: 'error',
        };
        return throwError(() => backendProviderUpdateError);
      }),
    );
  }

  public updateBackendProvider(payload: Partial<BackendProvider>) {
    return this.api.updateBackendProvider(payload).pipe(
      tap((resp) => {
        this.backendProvider.set(resp);
      }),
      catchError(() => {
        const backendProviderUpdateError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_BACKEND_PROVIDER_ERROR,
          severity: 'error',
        };
        return throwError(() => backendProviderUpdateError);
      }),
    );
  }

  public addEdi(ediKey: EdiKey) {
    console.debug('Adding EDI key:', ediKey);
    return this.api.addEdi(ediKey).pipe(
      tap(() => {
        this.organizationKeys.update((keys) => [...keys, ediKey]);
      }),
      catchError(() => {
        const ediKeyError: CRError = {
          errorCode: ChargeRepayErrorCodes.ADD_EDI_KEY_ERROR,
          severity: 'error',
        };
        return throwError(() => ediKeyError);
      }),
    );
  }

  public removeEdi(key: string, force: boolean) {
    console.debug('Removing EDI key:', key);
    return this.api.removeEdi(key, force).pipe(
      tap(() => {
        this.organizationKeys.update((keys) => keys.filter((k) => k.key !== key));
      }),
      catchError((err: CRAPIError) => {
        if (err.error_code === 'emobility.KEYS_STILL_USED') {
          const ediKeyInUseError: CRError = {
            errorCode: ChargeRepayErrorCodes.EDI_KEY_STILL_IN_USE,
            severity: 'error',
          };
          return throwError(() => ediKeyInUseError);
        }

        const ediKeyError: CRError = {
          errorCode: ChargeRepayErrorCodes.REMOVE_EDI_KEY_ERROR,
          severity: 'error',
        };

        return throwError(() => ediKeyError);
      }),
    );
  }

  public updateMileageSettings(request: UpdateMileageSettingsRequest) {
    console.debug('Updating mileage settings:', request);
    return this.api.updateOrganization(request).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const mileageSettingsError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_MILEAGE_SETTINGS_ERROR,
          severity: 'error',
        };
        return throwError(() => mileageSettingsError);
      }),
    );
  }

  public updateChargeAssignSettings(request: UpdateChargeAssignSettingsRequest) {
    console.debug('Updating charge assign settings:', request);
    return this.api.updateOrganization(request).pipe(
      tap((resp) => {
        this.organization.set(resp);
      }),
      catchError(() => {
        const chargeAssignSettingsError: CRError = {
          errorCode: ChargeRepayErrorCodes.UPDATE_CHARGE_ASSIGN_SETTINGS_ERROR,
          severity: 'error',
        };
        return throwError(() => chargeAssignSettingsError);
      }),
    );
  }

  // Omit yearMonth and every charging event is returned.
  public getChargingEvents(yearMonth?: string) {
    return this.api.getChargingEvents(yearMonth).pipe(
      catchError(() => {
        const chargingEventsError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_CHARGING_EVENTS_ERROR,
          severity: 'error',
        };
        return throwError(() => chargingEventsError);
      }),
    );
  }

  public getChargingEventsByRange(startDate: string, endDate: string) {
    return this.api.getChargingEventsByRange(startDate, endDate).pipe(
      catchError(() => {
        const chargingEventsError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_CHARGING_EVENTS_ERROR,
          severity: 'error',
        };
        return throwError(() => chargingEventsError);
      }),
    );
  }

  public downloadChargingEventsCsvByRange(startDate: string, endDate: string, uuid?: string) {
    return this.api.getChargingEventsCsv({ startDate, endDate, uuid }).pipe(
      map((response) => {
        if (!response.body) {
          throw new Error('No content');
        }
        return response.body;
      }),
      catchError(() => {
        const csvError: CRError = {
          errorCode: ChargeRepayErrorCodes.DOWNLOAD_CHARGING_EVENTS_CSV_ERROR,
          severity: 'error',
        };
        return throwError(() => csvError);
      }),
    );
  }

  // Omit yearMonth and every event for this charge point is returned.
  public getChargingEventsByChargePoint(cp: string, yearMonth?: string) {
    return this.api.getChargingEventsByChargePoint(cp, yearMonth).pipe(
      catchError(() => {
        const chargingEventsError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_CHARGING_EVENTS_ERROR,
          severity: 'error',
        };
        return throwError(() => chargingEventsError);
      }),
    );
  }

  public getDriverKeys(userId: string) {
    return this.api.getDriverKeys(userId).pipe(
      tap((keys) => this.currentDriverKeys.set(keys)),
      catchError(() => {
        this.currentDriverKeys.set([]);
        const driverKeysError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_DRIVER_KEYS_ERROR,
          severity: 'error',
        };
        return throwError(() => driverKeysError);
      }),
    );
  }

  public updateDriverSubscriptionStatus(userId: string, hasSubscription: boolean): void {
    this.drivers.update((drivers) =>
      drivers.map((d) =>
        d.user_id === userId ? { ...d, driver_has_subscription: hasSubscription } : d,
      ),
    );
  }

  public deletePricePolicy() {
    return this.api.deletePricePolicy().pipe(
      tap(() => {
        this.organization.update((org) => {
          if (!org) {
            return org;
          }
          return {
            ...org,
            price_policy: {
              ...org.price_policy,
              upcoming: null,
            },
          };
        });
      }),
      catchError(() => {
        const pricePolicyError: CRError = {
          errorCode: ChargeRepayErrorCodes.CREATE_PRICE_POLICY_ERROR,
          severity: 'error',
        };
        return throwError(() => pricePolicyError);
      }),
    );
  }

  public createPricePolicy(request: CreatePricePolicyRequest) {
    return this.api.createPricePolicy(request).pipe(
      tap((response) => {
        const upcoming = this.toStoredPricePolicy(response, request);
        this.organization.update((org) => {
          if (!org) {
            return org;
          }
          return {
            ...org,
            price_policy: {
              ...org.price_policy,
              upcoming,
            },
          };
        });
      }),
      catchError(() => {
        const pricePolicyError: CRError = {
          errorCode: ChargeRepayErrorCodes.CREATE_PRICE_POLICY_ERROR,
          severity: 'error',
        };
        return throwError(() => pricePolicyError);
      }),
    );
  }

  public loadFlatratePrices() {
    return this.api.getFlatratePrices().pipe(
      tap((prices) => this.flatratePrices.set(prices)),
      catchError(() => {
        this.flatratePrices.set([]);
        const flatratePricesError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_FLATRATE_PRICES_ERROR,
          severity: 'error',
        };
        return throwError(() => flatratePricesError);
      }),
    );
  }

  public getLatestPrice(uuid: string) {
    return this.api.getLatestPrice(uuid).pipe(
      tap((price) => this.currentDriverLatestPrice.set(price)),
      catchError(() => {
        this.currentDriverLatestPrice.set(null);
        const latestPriceError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_LATEST_PRICE_ERROR,
          severity: 'error',
        };
        return throwError(() => latestPriceError);
      }),
    );
  }

  public getChargingStationBackendData(uuid: string) {
    return this.api.getChargingStationBackendData(uuid).pipe(
      tap((data) => this.currentDriverChargingStationBackendData.set(data)),
      catchError(() => {
        this.currentDriverChargingStationBackendData.set(null);
        const backendDataError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_CHARGING_STATION_BACKEND_DATA_ERROR,
          severity: 'error',
        };
        return throwError(() => backendDataError);
      }),
    );
  }

  public getChargePointDetail(uuid: string) {
    return this.api.getChargePointDetail(uuid).pipe(
      tap((data) => this.currentDriverChargePointDetail.set(data)),
      catchError(() => {
        this.currentDriverChargePointDetail.set(null);
        const error: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_CHARGE_POINT_DETAIL_ERROR,
          severity: 'error',
        };
        this.errorHandler.handleError(error);
        return throwError(() => error);
      }),
    );
  }

  public saveChargingStationBackendData(
    uuid: string,
    payload: ChargingStationBackendDataRequest,
    isUpdate: boolean,
  ) {
    const previous = this.currentDriverChargingStationBackendData();

    // Set the changes immidiately (optimistic) but recorver in case of error
    if (previous) {
      this.currentDriverChargingStationBackendData.set({ ...previous, ...payload });
    }

    const call$ = isUpdate
      ? this.api.updateChargingStationBackendData(uuid, payload)
      : this.api.createChargingStationBackendData(uuid, payload);

    return call$.pipe(
      switchMap(() => this.api.getChargingStationBackendData(uuid)),
      tap((data) => this.currentDriverChargingStationBackendData.set(data)),
      catchError(() => {
        // recover the previous state if the update fails, this is important to avoid having the UI in a inconsistent state with wrong data or show the empty case text
        this.currentDriverChargingStationBackendData.set(previous);
        const saveError: CRError = {
          errorCode: ChargeRepayErrorCodes.SAVE_CHARGING_STATION_BACKEND_DATA_ERROR,
          severity: 'error',
        };
        return throwError(() => saveError);
      }),
    );
  }

  getMileagesOfCar(carId: string) {
    return this.api.getMileagesOfCar(carId).pipe(
      catchError(() => {
        const mileagesError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_MILEAGES_ERROR,
          severity: 'error',
        };
        return throwError(() => mileagesError);
      }),
    );
  }

  public testBackendProviderConnection(uuid: string) {
    return this.api.testBackendProviderConnection(uuid).pipe(
      catchError(() => {
        const error: CRError = {
          errorCode: ChargeRepayErrorCodes.TEST_BACKEND_PROVIDER_CONNECTION_ERROR,
          severity: 'error',
        };
        return throwError(() => error);
      }),
    );
  }

  private createDrivers$() {
    return this.api.getDrivers().pipe(
      tap((drivers) => {
        this.drivers.set(drivers);
      }),
      catchError(() => {
        this.drivers.set([]);
        this.handleDriversError();
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  private createChargePoints$() {
    return this.api.getChargePoints().pipe(
      tap((cp) => {
        this.chargePoints.set(cp);
      }),
      catchError(() => {
        this.chargePoints.set([]);
        this.handleChargePointsError();
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  private createDriverOrganization$() {
    return this.api.getDriverOrganization().pipe(
      tap((dO) => {
        this.organization.set(dO);
      }),
      catchError(() => {
        this.organization.set(null);
        this.handleDriverOrganizationError();
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  private createOrganizationKeys$() {
    return this.api.getOrganizationKeys().pipe(
      tap((keys) => {
        this.organizationKeys.set(keys);
      }),
      catchError(() => {
        this.organizationKeys.set([]);
        const keysError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_ORGANIZATION_KEYS_ERROR,
          severity: 'error',
        };
        this.errorHandler.handleError(keysError);
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  private calculateExtendedDrivers() {
    const drivers = this.drivers();
    const cps = this.chargePoints();
    const _extendedDrivers: DriverExtended[] = [];

    drivers.forEach((d) => {
      const cpMatch = cps.find((cp) => cp.driver_id === d.user_id);

      const _extendedDriver: DriverExtended = {
        ...d,
      };

      if (cpMatch) {
        _extendedDriver.wbEndpointId = cpMatch.endpoint_id;
        _extendedDriver.wbUUID = cpMatch.uuid;
      }

      _extendedDrivers.push(_extendedDriver);
    });

    return _extendedDrivers;
  }

  private pickNewestPrice(prices: Price[] | undefined | null): Price | null {
    if (!prices?.length) return null;
    return prices.reduce((latest, p) =>
      Date.parse(p.timestamp) > Date.parse(latest.timestamp) ? p : latest,
    );
  }

  private createBackendProvider$() {
    return this.api.getBackendProvider().pipe(
      tap((bp) => {
        if (bp.provider_id) {
          this.backendProvider.set(bp);
        }
      }),
      catchError((err) => {
        // Only handle 5xx errors, null errors (4xx) are silently ignored
        if (err !== null) {
          this.handleBackendProviderError();
        }
        this.backendProvider.set(null);
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  private createExports$() {
    return this.api.getExports().pipe(
      tap((exports) => {
        this.exports.set(exports);
      }),
      catchError((err) => {
        if (err !== null) {
          this.handleExportError();
        }
        this.exports.set([]);
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  public refreshExports() {
    return this.createExports$();
  }

  private createReimbursements$() {
    return this.api.getReimbursements().pipe(
      tap((reimbursements) => {
        this.reimbursements.set(reimbursements);
      }),
      catchError((err) => {
        if (err !== null) {
          this.handleReimbursementsError();
        }
        this.reimbursements.set([]);
        return EMPTY;
      }),
      ignoreElements(),
    );
  }

  public loadReimbursements() {
    return this.createReimbursements$();
  }

  public downloadExport(documentId: string) {
    return this.api.getExport(documentId).pipe(
      map((response) => {
        if (!response.body) {
          throw new Error('No content');
        }
        return response.body;
      }),
      catchError(() => {
        const downloadExportError: CRError = {
          errorCode: ChargeRepayErrorCodes.DOWNLOAD_EXPORT_ERROR,
          severity: 'error',
        };
        return throwError(() => downloadExportError);
      }),
    );
  }

  public downloadExportPreview(yearMonth: string, locale: string) {
    return this.api.postOrganizationExport(yearMonth, locale, true).pipe(
      map((response) => {
        if (!response.body) {
          throw new Error('No content');
        }
        return response.body;
      }),
      catchError(() => {
        const downloadExportError: CRError = {
          errorCode: ChargeRepayErrorCodes.DOWNLOAD_EXPORT_ERROR,
          severity: 'error',
        };
        return throwError(() => downloadExportError);
      }),
    );
  }

  public startBillingExport(yearMonth: string, locale: string) {
    return this.api.postOrganizationExport(yearMonth, locale, false).pipe(
      map((response) => {
        if (!response.body) {
          throw new Error('No content');
        }
        return response.body;
      }),
      tap(() => this.createExports$().subscribe()),
      catchError((apiError: CRAPIError) => {
        let errorCode: ChargeRepayErrorCodes;
        switch (apiError?.error_code) {
          case 'emobility.ALREADY_EXPORTED_ERROR':
            errorCode = ChargeRepayErrorCodes.ALREADY_EXPORTED_ERROR;
            break;
          case 'emobility.AUTOMATIC_EXPORT_ACTIVE':
            errorCode = ChargeRepayErrorCodes.AUTOMATIC_EXPORT_ACTIVE;
            break;
          case 'emobility.EXPORT_ERROR':
            errorCode = ChargeRepayErrorCodes.EXPORT_ERROR;
            break;
          default:
            errorCode = ChargeRepayErrorCodes.DOWNLOAD_EXPORT_ERROR;
        }
        return throwError(() => ({ errorCode, severity: 'error' }) as CRError);
      }),
    );
  }

  public downloadEnergyContract(uuid: string, priceId: number) {
    return this.api.getEnergyContract(uuid, priceId).pipe(
      map((response) => {
        const blob = response.body!;
        const xFilename = response.headers.get('x-filename') ?? '';
        const extension = xFilename.includes('.') ? xFilename.split('.').pop() : null;
        const filename = extension ? xFilename : 'contract';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }),
      catchError(() => {
        const energyContractError: CRError = {
          errorCode: ChargeRepayErrorCodes.GET_ENERGY_CONTRACT_ERROR,
          severity: 'error',
        };
        return throwError(() => energyContractError);
      }),
    );
  }

  public updateDriverKey(userId: string, payload: DriverKeyFull) {
    return this.api
      .updateDriverKey(userId, payload)
      .pipe(catchError((err: CRAPIError) => this.mapDriverKeySaveError(err)));
  }

  public createDriverKey(userId: string, payload: DriverKey) {
    return this.api
      .createDriverKey(userId, payload)
      .pipe(catchError((err: CRAPIError) => this.mapDriverKeySaveError(err)));
  }

  private toStoredPricePolicy(
    response: OrganizationPricePolicy | null,
    request: CreatePricePolicyRequest,
  ): OrganizationPricePolicy {
    if (
      response &&
      typeof response.price_policy === 'string' &&
      typeof response.year === 'number'
    ) {
      return response;
    }

    return {
      organization_id: this.organization()?.organization_id ?? '',
      organization_price_policy_id:
        this.organization()?.price_policy?.upcoming?.organization_price_policy_id ?? '',
      price_policy: request.price_policy,
      year: request.year,
    };
  }

  private mapDriverKeySaveError(err: CRAPIError | CRError): Observable<never> {
    const errorCode = 'errorCode' in err ? err.errorCode : err.error_code;
    console.log('errorCode', errorCode);
    if (errorCode === ChargeRepayErrorCodes.INVALID_INPUT_ERROR) {
      console.log('err', err);
      const invalidInputError: CRError = {
        errorCode: ChargeRepayErrorCodes.INVALID_INPUT_ERROR,
        severity: 'error',
        message: err.message,
      };
      return throwError(() => invalidInputError);
    }

    const saveDriverKeyError: CRError = {
      errorCode: ChargeRepayErrorCodes.SAVE_DRIVER_KEY_ERROR,
      severity: 'error',
    };
    return throwError(() => saveDriverKeyError);
  }

  // This function is currently not used but will be useful in the future when we want to display export related information in the frontend

  private handleDriversError(): void {
    const noDriversError: CRError = {
      errorCode: ChargeRepayErrorCodes.GET_DRIVER_ERROR,
      severity: 'panic',
    };
    this.errorHandler.handleError(noDriversError);
  }

  private handleChargePointsError(): void {
    const noChargePointsError: CRError = {
      errorCode: ChargeRepayErrorCodes.GET_CHARGEPOINTS_ERROR,
      severity: 'panic',
    };
    this.errorHandler.handleError(noChargePointsError);
  }

  private handleDriverOrganizationError(): void {
    const driverOrganizationError: CRError = {
      errorCode: ChargeRepayErrorCodes.GET_DRIVER_ORGANIZATION_ERROR,
      severity: 'panic',
    };
    this.errorHandler.handleError(driverOrganizationError);
  }

  private handleBackendProviderError(): void {
    const backendProviderError: CRError = {
      errorCode: ChargeRepayErrorCodes.GET_BACKEND_PROVIDER_ERROR,
      severity: 'error',
    };
    this.errorHandler.handleError(backendProviderError);
  }

  private handleExportError(): void {
    const exportError: CRError = {
      errorCode: ChargeRepayErrorCodes.GET_EXPORTS_ERROR,
      severity: 'error',
    };
    this.errorHandler.handleError(exportError);
  }

  private handleReimbursementsError(): void {
    const reimbursementsError: CRError = {
      errorCode: ChargeRepayErrorCodes.GET_REIMBURSEMENTS_ERROR,
      severity: 'error',
    };
    this.errorHandler.handleError(reimbursementsError);
  }
}
